/**
 * Responsibility: Centralized action execution orchestration for the UI layer.
 * Scope: Action initiation events, execution coordination, callback notification.
 */
import { dispatcher } from '../app-states.js'
import { TOPICS } from '@anyquest/shared-core'
import type { ModalAction } from '@anyquest/shared-core'
import * as gameStore from '../game-store.js'

export interface ActionInitiatedEvent {
  action: ModalAction
  context: {
    initiatorId?: string
    targetName?: string
    targetType?: 'companion' | 'npc' | 'item' | 'location'
    [key: string]: unknown
  }
  callback: (result: ActionResult) => void
}

export interface ActionResult {
  success: boolean
  action?: ModalAction
  error?: string
  data?: unknown
}

export type ActionExecutionContext = ActionInitiatedEvent['context']

function resolveTopicName(topicString: string): string {
  switch (String(topicString ?? '').trim()) {
    case TOPICS.ui.dialogOpen:
    case 'ui:dialogOpen':
      return TOPICS.ui.dialogOpen
    case TOPICS.trade.open:
    case 'trade:open':
      return TOPICS.trade.open
    default:
      return String(topicString ?? '').trim()
  }
}

function topicPayload(action: ModalAction, context: ActionExecutionContext): Record<string, unknown> {
  const payload = (action?.topicData && typeof action.topicData === 'object' && !Array.isArray(action.topicData))
    ? { ...(action.topicData as Record<string, unknown>) }
    : {}

  return {
    ...payload,
    actionContext: {
      initiatorId: context?.initiatorId,
      targetName: context?.targetName,
      targetType: context?.targetType,
      source: action?.source,
      mode: action?.mode,
      category: action?.category,
      explicitTargets: action?.explicitTargets,
      tool: action?.tool,
      tags: action?.tags,
    },
  }
}

function handleActionOptions(action: ModalAction) {
  return {
    source: action?.source,
    mode: action?.mode,
    category: action?.category,
    tool: action?.tool,
    explicitTargets: action?.explicitTargets,
    tags: action?.tags,
  }
}

export async function runUiAction(action: ModalAction, context: ActionExecutionContext = {}): Promise<void> {
  if (!action) return

  if (action.topic) {
    const topic = resolveTopicName(String(action.topic))
    dispatcher.trigger('action-manager', topic, topicPayload(action, context))
    return
  }

  const input = String(action.input ?? '').trim()
  if (!input) return
  await gameStore.handleAction(input, undefined, handleActionOptions(action))
}

export class ActionManager {
  private static instance: ActionManager

  private constructor() {
    this.setupListeners()
  }

  static getInstance(): ActionManager {
    if (!ActionManager.instance) {
      ActionManager.instance = new ActionManager()
    }
    return ActionManager.instance
  }

  private setupListeners(): void {
    dispatcher.on(TOPICS.action.initiated, (_origin, event: ActionInitiatedEvent) => {
      this.executeAction(event)
    })
  }

  /**
   * Execute an action: dispatch its topic event (if it has one) or call the callback
   * with success. The action's topic event handler will do the actual work.
   */
  private async executeAction(event: ActionInitiatedEvent): Promise<void> {
    const { action, context, callback } = event
    try {
      await runUiAction(action, context)
      callback({ success: true, action })
    } catch (error) {
      callback({
        success: false,
        action,
        error: `Action dispatch failed: ${String(error)}`,
      })
    }
  }
}

// Initialize singleton on module load
export const actionManager = ActionManager.getInstance()
